import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { DateTime } from 'luxon';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import { useAuthSync } from '../../hooks/useAuthSync';
import AppointmentService from '../../services/appointmentService';
import CalendarBlockService from '../../services/calendarBlockService';
import PatientService from '../../services/patientService';
import UserService from '../../services/userService';
import LocationService from '../../services/locationService';
import PreferencesService from '../../services/preferencesService';
import { isValidAppointmentDate } from '../../utils/dateUtils';
import Button from '../common/Button';
import PatientAutocomplete from '../common/PatientAutocomplete';
import './AppointmentForm.css';

const AVAILABILITY_DEBOUNCE_MS = 300;

const AppointmentForm = ({ 
  appointment = null, 
  isModal = false, 
  onSuccess, 
  onCancel, 
  onClose,
  onDelete, 
  defaultPatientId,
  defaultDate
}) => {
  const auth = useAuth();
  const { timezone, isLoading: timezoneLoading } = useOrganizationTimezone();
  const { hasPermission, user: authUser } = useAuthSync();
  const canCreateForOthers = hasPermission('appointments:createforothers');
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const [formData, setFormData] = useState({
    patientId: defaultPatientId || '',
    doctorId: appointment ? '' : (authUser?.id || ''), // Default to current user when creating
    locationId: '',
    date: defaultDate || '',
    startTime: '',
    duration: 30, // duration in minutes
    status: 'scheduled', // scheduled, confirmed, cancelled
    procedure: '',
    notes: ''
  });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // Debounced copy of formData.date, empty while the date is incomplete,
  // so availability is only fetched once the user is done typing
  const [availabilityDate, setAvailabilityDate] = useState(() => (isValidAppointmentDate(defaultDate) ? defaultDate : ''));
  const [showCustomTime, setShowCustomTime] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});
  const [preferences, setPreferences] = useState(null);
  const [loadingPreferences, setLoadingPreferences] = useState(true);

  const getOriginalAppointmentSelection = () => {
    if (!appointment || timezoneLoading) {
      return null;
    }

    const originalDateTime = DateTime.fromISO(appointment.appointmentDateTime).setZone(timezone);

    if (!originalDateTime.isValid) {
      return null;
    }

    return {
      date: originalDateTime.toISODate(),
      startTime: originalDateTime.toFormat('HH:mm'),
      duration: appointment.durationMinutes || 30,
      doctorId: appointment.doctorId || appointment.userId || '',
      locationId: appointment.locationId || ''
    };
  };

  const getMinutesFromTimeString = (timeValue) => {
    const [hours = 0, minutes = 0] = (timeValue || '00:00').split(':').map(Number);
    return hours * 60 + minutes;
  };

  const buildSlotConflict = (slotStartMinutes, slotEndMinutes, appointmentIntervals, blockIntervals) => {
    const conflictingAppointments = appointmentIntervals.filter(({ startMinutes, endMinutes }) => (
      slotStartMinutes < endMinutes && slotEndMinutes > startMinutes
    ));
    const matchingBlocks = blockIntervals.filter(({ startMinutes, endMinutes }) => (
      slotStartMinutes < endMinutes && slotEndMinutes > startMinutes
    ));

    const hasAppointmentConflict = conflictingAppointments.length > 0;
    const hasBlockConflict = matchingBlocks.length > 0;

    if (hasAppointmentConflict && hasBlockConflict) {
      const firstBlock = matchingBlocks[0];
      return {
        isConflict: true,
        conflictType: 'both',
        conflictTitle: firstBlock.reason
          ? `Ocupado: bloqueo (${firstBlock.reason}) y otra cita`
          : 'Ocupado: bloqueo y otra cita',
        conflictingAppointments,
        conflictingBlocks: matchingBlocks
      };
    }

    if (hasBlockConflict) {
      const firstBlock = matchingBlocks[0];
      return {
        isConflict: true,
        conflictType: 'block',
        conflictTitle: firstBlock.reason
          ? `Ocupado por bloqueo: ${firstBlock.reason}`
          : 'Ocupado por bloqueo',
        conflictingAppointments: [],
        conflictingBlocks: matchingBlocks
      };
    }

    if (hasAppointmentConflict) {
      return {
        isConflict: true,
        conflictType: 'appointment',
        conflictTitle: 'Conflicto con otra cita',
        conflictingAppointments,
        conflictingBlocks: []
      };
    }

    return {
      isConflict: false,
      conflictType: null,
      conflictTitle: 'Disponible',
      conflictingAppointments: [],
      conflictingBlocks: []
    };
  };

  const loadSchedulingConstraints = useCallback(async ({ date, doctorId, locationId }) => {
    if (!isValidAppointmentDate(date)) {
      return { appointmentIntervals: [], blockIntervals: [] };
    }

    const [appointmentsOnDate, doctorBlocks] = await Promise.all([
      AppointmentService.getAppointmentsByDate(date, auth.user.access_token),
      doctorId
        ? CalendarBlockService.getCalendarBlockOccurrences(
            doctorId,
            { startDate: date, endDate: date, timezone },
            auth.user.access_token
          )
        : Promise.resolve([])
    ]);

    const baseAppointments = (appointmentsOnDate || []).filter((appt) => {
      const isNotCancelled = appt.status !== 'cancelled';
      const isNotCurrent = !appointment || appt.id !== appointment.id;
      return isNotCancelled && isNotCurrent;
    });

    const doctorAppointments = doctorId
      ? baseAppointments.filter((appt) => (appt.doctorId || appt.userId) === doctorId)
      : [];

    const locationAppointments = locationId
      ? baseAppointments.filter((appt) => appt.locationId === locationId)
      : [];

    const seenIds = new Set();
    const relevantAppointments = [...doctorAppointments, ...locationAppointments].filter((appt) => {
      if (seenIds.has(appt.id)) return false;
      seenIds.add(appt.id);
      return true;
    });

    const appointmentIntervals = relevantAppointments
      .map((appt) => {
        const apptDateTime = DateTime.fromISO(appt.appointmentDateTime).setZone(timezone);

        if (!apptDateTime.isValid) {
          return null;
        }

        const startMinutes = apptDateTime.hour * 60 + apptDateTime.minute;
        return {
          id: appt.id,
          appointmentDateTime: appt.appointmentDateTime,
          startMinutes,
          endMinutes: startMinutes + (appt.durationMinutes || 30),
          durationMinutes: appt.durationMinutes || 30,
          status: appt.status,
          patientId: appt.patientId,
          patientName: appt.patient
            ? `${appt.patient.firstName || ''} ${appt.patient.lastName || ''}`.trim() || appt.patient.name
            : null,
          doctorId: appt.doctorId || appt.userId || null,
          doctorName: appt.doctor ? appt.doctor.name : null,
          locationId: appt.locationId || null,
          procedure: appt.procedure || ''
        };
      })
      .filter(Boolean);

    const blockIntervals = (doctorBlocks || [])
      .map((block) => {
        const start = DateTime.fromISO(block.startDateTime, { setZone: true }).setZone(timezone);
        const end = DateTime.fromISO(block.endDateTime, { setZone: true }).setZone(timezone);

        if (!start.isValid || !end.isValid) {
          return null;
        }

        const startMinutes = block.allDay ? 0 : (start.hour * 60 + start.minute);
        const endMinutes = block.allDay
          ? 24 * 60
          : (end.hasSame(start, 'day') ? (end.hour * 60 + end.minute) : 24 * 60);

        return {
          startMinutes,
          endMinutes,
          reason: block.reason || ''
        };
      })
      .filter((block) => block && block.endMinutes > block.startMinutes);

    return { appointmentIntervals, blockIntervals };
  }, [appointment, auth.user.access_token, timezone]);

  const isEditingOriginalTimeSelection = () => {
    const originalSelection = getOriginalAppointmentSelection();

    if (!originalSelection) {
      return false;
    }

    return (
      formData.date === originalSelection.date &&
      formData.startTime === originalSelection.startTime &&
      parseInt(formData.duration, 10) === originalSelection.duration &&
      (formData.doctorId || '') === originalSelection.doctorId &&
      (formData.locationId || '') === originalSelection.locationId
    );
  };

  // Load user preferences for day view hours
  useEffect(() => {
    const loadPreferences = async () => {
      if (!auth.user?.access_token) return;
      
      try {
        const prefs = await PreferencesService.getUserPreferences(auth.user.access_token);
        setPreferences(prefs);
      } catch (error) {
        console.error('Error loading preferences:', error);
        // Use defaults if preferences can't be loaded
        setPreferences(PreferencesService.getDefaultPreferences());
      } finally {
        setLoadingPreferences(false);
      }
    };

    loadPreferences();
  }, [auth.user?.access_token]);

  // Load appointment data if editing
  useEffect(() => {
    if (appointment && !timezoneLoading) {
      const dt = DateTime.fromISO(appointment.appointmentDateTime).setZone(timezone);
      const dateStr = dt.toISODate();
      const timeStr = dt.toISOTime().substring(0, 5);
      
      setFormData({
        patientId: appointment.patientId || '',
        doctorId: appointment.doctorId || appointment.userId || '', // Handle backward compatibility
        locationId: appointment.locationId || '',
        date: dateStr,
        startTime: timeStr,
        duration: appointment.durationMinutes || 30,
        status: appointment.status || 'scheduled',
        procedure: appointment.procedure || '',
        notes: appointment.notes || ''
      });

      // If we have patient data in the appointment, set it
      if (appointment.patient) {
        setSelectedPatient(appointment.patient);
      } else if (appointment.patientId) {
        // If we only have patientId, we'll need to load the patient data
        // This will be handled by the PatientAutocomplete component when patients are loaded
      }
    }
  }, [appointment, timezone, timezoneLoading]);

  // If creating and defaultPatientId changes, set it when patient not selected yet
  useEffect(() => {
    if (!appointment && defaultPatientId && !formData.patientId) {
      setFormData(prev => ({ ...prev, patientId: defaultPatientId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPatientId, appointment]);

  // Load patient data if we have patientId but no selected patient (edit mode)
  useEffect(() => {
    const loadPatientData = async () => {
      if (formData.patientId && !selectedPatient && auth.user?.access_token) {
        try {
          const patientData = await PatientService.getPatientById(formData.patientId, auth.user.access_token);
          setSelectedPatient(patientData);
        } catch (error) {
          console.error('Error loading patient data:', error);
        }
      }
    };

    loadPatientData();
  }, [formData.patientId, selectedPatient, auth.user?.access_token]);

  // If creating and defaultDate changes, set it when date not selected yet
  useEffect(() => {
    if (!appointment && defaultDate && !formData.date) {
      setFormData(prev => ({ ...prev, date: defaultDate }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDate, appointment]);

  // Load doctors list
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setIsLoadingDoctors(true);

        if (canCreateForOthers) {
          // Full list: fetch all org doctors
          const result = await UserService.getDoctors(auth.user?.access_token);
          setDoctors(result.data || []);
        } else {
          // Restricted: only the current user
          const selfDoctor = authUser
            ? [{ id: authUser.id, name: authUser.name, licenseId: authUser.licenseId || null }]
            : [];
          setDoctors(selfDoctor);
          // Pre-select the current user as doctor
          if (authUser?.id) {
            setFormData(prev => ({ ...prev, doctorId: authUser.id }));
          }
        }
      } catch (error) {
        console.error('Error loading doctors:', error);
        setMessage({ type: 'error', text: 'Error al cargar la lista de doctores' });
      } finally {
        setIsLoadingDoctors(false);
      }
    };

    if (auth.user?.access_token) {
      loadDoctors();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.access_token, canCreateForOthers]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setIsLoadingLocations(true);
        const result = await LocationService.getAllLocations(auth.user?.access_token);
        const organizationLocations = result.data || [];
        setLocations(organizationLocations);

        setFormData((prev) => {
          if (appointment && appointment.locationId) {
            return prev;
          }

          if (prev.locationId) {
            return prev;
          }

          return {
            ...prev,
            locationId: organizationLocations[0]?.id || ''
          };
        });
      } catch (error) {
        console.error('Error loading locations:', error);
        setMessage({ type: 'error', text: 'Error al cargar las ubicaciones' });
      } finally {
        setIsLoadingLocations(false);
      }
    };

    if (auth.user?.access_token) {
      loadLocations();
    }
  }, [auth.user?.access_token, appointment]);

  useEffect(() => {
    if (!isValidAppointmentDate(formData.date)) {
      setAvailabilityDate('');
      return;
    }
    const timeoutId = setTimeout(() => setAvailabilityDate(formData.date), AVAILABILITY_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [formData.date]);

  // Generate available slots when date or duration changes (doctor is optional)
  useEffect(() => {
    const generateSlots = async () => {
      if (!availabilityDate || !formData.duration || !auth.user?.access_token || loadingPreferences || timezoneLoading) {
        setAvailableSlots([]);
        return;
      }

      setLoadingSlots(true);

      try {
        const { appointmentIntervals, blockIntervals } = await loadSchedulingConstraints({
          date: availabilityDate,
          doctorId: formData.doctorId,
          locationId: formData.locationId
        });

        // Get working hours from preferences or use defaults
        const startHourStr = preferences?.dayViewStartTime || '08:00';
        const endHourStr = preferences?.dayViewEndTime || '18:00';
        
        const [startH, startM] = startHourStr.split(':').map(Number);
        const [endH, endM] = endHourStr.split(':').map(Number);
        
        const startTimeMinutes = startH * 60 + startM;
        const endTimeMinutes = endH * 60 + endM;
        const duration = parseInt(formData.duration);

        const slots = [];
        let currentMinutes = startTimeMinutes;

        while (currentMinutes + duration <= endTimeMinutes) {
          const h = Math.floor(currentMinutes / 60);
          const m = currentMinutes % 60;
          const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
          
          // Calculate slot end time
          const slotEndMinutes = currentMinutes + duration;
          const slotStartMinutes = currentMinutes;
          const conflictState = buildSlotConflict(
            slotStartMinutes,
            slotEndMinutes,
            appointmentIntervals,
            blockIntervals
          );

          slots.push({
            time: timeStr,
            ...conflictState
          });

          // Step by duration as requested ("every X minutes")
          currentMinutes += duration;
        }

        setAvailableSlots(slots);
      } catch (error) {
        console.error('Error generating slots:', error);
      } finally {
        setLoadingSlots(false);
      }
    };

    generateSlots();
  }, [availabilityDate, formData.doctorId, formData.locationId, formData.duration, auth.user?.access_token, loadSchedulingConstraints, preferences, loadingPreferences, timezoneLoading]);

  // Proactively detect conflicts on the currently selected time slot.
  // Skips the first run so the warning doesn't fire on initial mount before
  // the user has interacted with the form.
  const isInitialConflictCheck = useRef(true);
  useEffect(() => {
    if (isInitialConflictCheck.current) {
      isInitialConflictCheck.current = false;
      return;
    }

    const checkCurrentSlotConflict = async () => {
      if (
        !availabilityDate ||
        !formData.startTime ||
        !formData.duration ||
        !auth.user?.access_token ||
        loadingPreferences ||
        timezoneLoading
      ) {
        return;
      }

      try {
        const { appointmentIntervals, blockIntervals } = await loadSchedulingConstraints({
          date: availabilityDate,
          doctorId: formData.doctorId,
          locationId: formData.locationId
        });

        const startMinutes = getMinutesFromTimeString(formData.startTime);
        const duration = parseInt(formData.duration, 10);
        const endMinutes = startMinutes + duration;

        const conflictState = buildSlotConflict(
          startMinutes,
          endMinutes,
          appointmentIntervals,
          blockIntervals
        );

        if (conflictState.isConflict && conflictState.conflictType === 'appointment') {
          setMessage({
            type: 'warning',
            text: 'Este horario coincide con otra cita. ¿Deseas agendarla de todos modos?'
          });
        } else {
          setMessage((prev) => (prev.type === 'warning' ? { type: '', text: '' } : prev));
        }
      } catch (error) {
        console.error('Error checking slot conflict:', error);
      }
    };

    checkCurrentSlotConflict();
  }, [availabilityDate, formData.startTime, formData.duration, formData.doctorId, formData.locationId, auth.user?.access_token, loadingPreferences, timezoneLoading, loadSchedulingConstraints]);


  // Fix date/time input positioning on component mount
  useEffect(() => {
    const setupDateTimeInputs = () => {
      // Ensure date and time inputs have proper attributes
      if (dateInputRef.current) {
        const dateInput = dateInputRef.current;
        dateInput.style.position = 'relative';
        dateInput.style.overflow = 'visible';
        // Force webkit appearance for proper native controls
        dateInput.style.webkitAppearance = 'auto';
        dateInput.style.appearance = 'auto';
        // Remove any attributes that might prevent picker
        dateInput.removeAttribute('readonly');
        dateInput.setAttribute('autocomplete', 'off');
      }
      
      if (timeInputRef.current) {
        const timeInput = timeInputRef.current;
        timeInput.style.position = 'relative';
        timeInput.style.overflow = 'visible';
        // Force webkit appearance for proper native controls
        timeInput.style.webkitAppearance = 'auto';
        timeInput.style.appearance = 'auto';
        // Remove any attributes that might prevent picker
        timeInput.removeAttribute('readonly');
        timeInput.setAttribute('autocomplete', 'off');
      }
    };

    // Setup immediately and after a brief delay to ensure DOM is ready
    setupDateTimeInputs();
    const timeoutId = setTimeout(setupDateTimeInputs, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Editing any time-slot field invalidates a previously detected conflict.
    if (['date', 'startTime', 'duration', 'doctorId', 'locationId'].includes(name)) {
      setMessage((prev) => (prev.type === 'warning' ? { type: '', text: '' } : prev));
    }
  };

  // Handle date/time input focus to fix positioning issues
  const handleDateTimeInputFocus = (e) => {
    const input = e.target;
    const formGroup = input.closest('.form-group');
    const formContainer = input.closest('.appointment-form-container');
    
    // Add focus classes for CSS targeting
    if (formGroup) {
      formGroup.classList.add('datetime-focused');
    }
    if (formContainer) {
      formContainer.classList.add('has-datetime-focused');
    }
    
    // Ensure proper stacking and positioning
    setTimeout(() => {
      input.style.position = 'relative';
      input.style.zIndex = '99999';
      if (formGroup) {
        formGroup.style.overflow = 'visible';
        formGroup.style.zIndex = '99999';
      }
    }, 0);
  };

  const handleDateTimeInputBlur = (e) => {
    const input = e.target;
    const formGroup = input.closest('.form-group');
    const formContainer = input.closest('.appointment-form-container');
    
    // Remove focus classes
    if (formGroup) {
      formGroup.classList.remove('datetime-focused');
    }
    if (formContainer) {
      formContainer.classList.remove('has-datetime-focused');
    }
    
    // Reset styles
    setTimeout(() => {
      input.style.position = '';
      input.style.zIndex = '';
      if (formGroup) {
        formGroup.style.overflow = '';
        formGroup.style.zIndex = '';
      }
    }, 100);
  };

  // Handle mousedown to ensure proper interaction
  const handleDateTimeInputMouseDown = (e) => {
    const input = e.target;
    // Ensure the input is ready for interaction
    input.style.webkitAppearance = 'auto';
    input.style.appearance = 'auto';
    input.style.pointerEvents = 'auto';
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setFormData(prev => ({
      ...prev,
      patientId: patient ? patient.id : ''
    }));
    
    // Clear patient selection error
    if (errors.patientId) {
      setErrors(prev => ({
        ...prev,
        patientId: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedPatient || !formData.patientId.trim()) {
      newErrors.patientId = 'Debe seleccionar un paciente';
    }

    if (!formData.date) {
      newErrors.date = 'La fecha es requerida';
    } else if (!isValidAppointmentDate(formData.date)) {
      newErrors.date = 'Fecha inválida';
    }

    if (!formData.locationId) {
      newErrors.locationId = 'Debe seleccionar una ubicación';
    }

    if (!formData.startTime) {
      newErrors.startTime = 'La hora de inicio es requerida';
    }

    // Past date validation removed as per requirement

    if (!formData.duration || formData.duration < 15 || formData.duration > 480) {
      newErrors.duration = 'La duración debe estar entre 15 y 480 minutos';
    }

    if (formData.procedure && formData.procedure.length > 50) {
      newErrors.procedure = 'El procedimiento debe tener maximo 50 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayloadAndSubmit = async ({ force = false } = {}) => {
    const startDateTime = DateTime.fromISO(`${formData.date}T${formData.startTime}`, { zone: timezone });
    const appointmentDateTime = startDateTime.toUTC().toISO();

    const appointmentData = {
      patientId: formData.patientId,
      doctorId: formData.doctorId || null,
      locationId: formData.locationId,
      appointmentDateTime,
      durationMinutes: parseInt(formData.duration, 10),
      status: formData.status,
      procedure: formData.procedure.trim(),
      notes: formData.notes.trim()
    };

    if (appointment) {
      await AppointmentService.updateAppointment(
        appointment.id,
        appointmentData,
        auth.user?.access_token,
        { force }
      );
      setMessage({ type: 'success', text: 'Cita actualizada exitosamente' });
    } else {
      await AppointmentService.createAppointment(appointmentData, auth.user?.access_token, { force });
      setMessage({ type: 'success', text: 'Cita creada exitosamente' });
    }

    setTimeout(() => {
      if (onSuccess) {
        onSuccess();
      }
    }, 150);
  };

  const handleSubmit = async (e, options = {}) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    // If the conflict warning is already on screen, the user is confirming
    // they want to proceed despite the conflict — auto-force the submit so
    // the appointment is created immediately instead of re-surfacing the warning.
    const warningVisible = message.type === 'warning' && message.text.includes('coincide con otra cita');
    const force = !!options.force || warningVisible;

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let precheckConflictState = null;
      if (!isEditingOriginalTimeSelection()) {
        const { appointmentIntervals, blockIntervals } = await loadSchedulingConstraints({
          date: formData.date,
          doctorId: formData.doctorId,
          locationId: formData.locationId
        });
        const startMinutes = getMinutesFromTimeString(formData.startTime);
        const endMinutes = startMinutes + parseInt(formData.duration, 10);
        precheckConflictState = buildSlotConflict(startMinutes, endMinutes, appointmentIntervals, blockIntervals);

        if (precheckConflictState.isConflict && !force) {
          // Block conflicts cannot be overridden (they are hard blocks set by the doctor).
          // Only appointment-to-appointment conflicts can be overbooked.
          if (precheckConflictState.conflictType === 'block' || precheckConflictState.conflictType === 'both') {
            setErrors((prev) => ({ ...prev, startTime: precheckConflictState.conflictTitle }));
            setMessage({ type: 'error', text: precheckConflictState.conflictTitle });
            setIsLoading(false);
            return;
          }

          setErrors((prev) => ({ ...prev, startTime: '' }));
          setMessage({
            type: 'warning',
            text: 'Este horario coincide con otra cita. ¿Deseas agendarla de todos modos?'
          });
          setIsLoading(false);
          return;
        }
      }

      await buildPayloadAndSubmit({ force });

    } catch (error) {
      console.error('Error saving appointment:', error);

      // Backend refused with 409 — surface the override UI instead of a hard error.
      if (error && error.status === 409 && Array.isArray(error.conflicts) && error.conflicts.length > 0) {
        setErrors((prev) => ({ ...prev, startTime: '' }));
        setMessage({
          type: 'warning',
          text: 'Este horario coincide con otra cita. ¿Deseas agendarla de todos modos?'
        });
        setIsLoading(false);
        return;
      }

      setMessage({
        type: 'error',
        text: error.message || 'Error al guardar la cita. Por favor, intente nuevamente.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (onClose) {
      onClose();
    }
  };

  const isDateValid = isValidAppointmentDate(formData.date);

  return (
    <div className={`appointment-form-container ${isModal ? 'modal' : ''}`}>
      {!isModal && (
        <div className="appointment-form-header">
          <h1>{appointment ? 'Editar Cita' : 'Nueva Cita'}</h1>
          <p>
            {appointment 
              ? 'Modifique los detalles de la cita existente' 
              : 'Complete los detalles para programar una nueva cita'
            }
          </p>
        </div>
      )}

      <form className="appointment-form" onSubmit={handleSubmit}>

        <div className="form-grid">
          {/* Patient Selection */}
          <div className="form-group autocomplete-container">
            <label htmlFor="patientId">
              Paciente <span className="required">*</span>
            </label>
            <PatientAutocomplete
              selectedPatient={selectedPatient}
              onPatientSelect={handlePatientSelect}
              placeholder="Seleccione un paciente"
              searchPlaceholder="Buscar paciente por nombre..."
              disabled={isLoading}
              className={errors.patientId ? 'error' : ''}
              showPatientId={true}
              showBirthDate={true}
              minSearchLength={2}
              maxResults={50}
            />
            {errors.patientId && <span className="error-text">{errors.patientId}</span>}
          </div>

          {/* Doctor Selection (Optional) */}
          <div className="form-group">
            <label htmlFor="doctorId">
              Doctor/Dentista
            </label>
            <select
              id="doctorId"
              name="doctorId"
              value={formData.doctorId}
              onChange={handleInputChange}
              className={errors.doctorId ? 'error' : ''}
              disabled={isLoadingDoctors || !canCreateForOthers}
            >
              <option value="">
                {isLoadingDoctors ? 'Cargando doctores...' : 'Sin asignar'}
              </option>
              {doctors.map(doctor => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name} {doctor.licenseId ? `(Cédula. ${doctor.licenseId})` : ''}
                </option>
              ))}
            </select>
            {errors.doctorId && <span className="error-text">{errors.doctorId}</span>}
          </div>

          {/* Only show location dropdown if there are 2 or more locations */}
          {locations.length >= 2 && (
            <div className="form-group">
              <label htmlFor="locationId">
                Ubicación <span className="required">*</span>
              </label>
              <select
                id="locationId"
                name="locationId"
                value={formData.locationId}
                onChange={handleInputChange}
                className={errors.locationId ? 'error' : ''}
                disabled={isLoadingLocations || isLoading}
              >
                <option value="">
                  {isLoadingLocations ? 'Cargando ubicaciones...' : 'Seleccione una ubicación'}
                </option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              {errors.locationId && <span className="error-text">{errors.locationId}</span>}
            </div>
          )}

          {/* Date */}
          <div className="form-group">
            <label htmlFor="date">
              Fecha <span className="required">*</span>
            </label>
            <input
              ref={dateInputRef}
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              onFocus={handleDateTimeInputFocus}
              onBlur={handleDateTimeInputBlur}
              onMouseDown={handleDateTimeInputMouseDown}
              className={errors.date ? 'error' : ''}
              autoComplete="off"
            />
            {errors.date && <span className="error-text">{errors.date}</span>}
          </div>

          {/* Duration */}
          <div className="form-group">
            <label htmlFor="duration">
              Duración (minutos)
            </label>
            <select
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              className={errors.duration ? 'error' : ''}
            >
              <option value={15}>15 minutos</option>
              <option value={30}>30 minutos</option>
              <option value={45}>45 minutos</option>
              <option value={60}>1 hora</option>
              <option value={90}>1 hora 30 minutos</option>
              <option value={120}>2 horas</option>
              <option value={180}>3 horas</option>
            </select>
            {errors.duration && <span className="error-text">{errors.duration}</span>}
          </div>

          {/* Start Time - Slot Selection */}
          <div className="form-group full-width time-slots-container">
            <label htmlFor="startTime">
              Hora de Inicio <span className="required">*</span>
            </label>

            {!isDateValid ? (
              <div className="time-selection-warning">
                {formData.date
                  ? 'Ingrese una fecha válida (año entre 2001 y 2099) para ver los horarios disponibles.'
                  : 'Seleccione una fecha para ver los horarios disponibles.'}
              </div>
            ) : (
              <>
                <div className="time-selection-controls">
                  <span className="slots-info">Horarios cada {formData.duration} min</span>
                  <button 
                    type="button" 
                    className="toggle-custom-time-btn"
                    onClick={() => setShowCustomTime(!showCustomTime)}
                  >
                    {showCustomTime ? 'Ver horarios disponibles' : 'Ingresar hora manual'}
                  </button>
                </div>

                {showCustomTime ? (
                  <input
                    ref={timeInputRef}
                    type="time"
                    id="startTime"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    onFocus={handleDateTimeInputFocus}
                    onBlur={handleDateTimeInputBlur}
                    onMouseDown={handleDateTimeInputMouseDown}
                    className={`manual-time-input ${errors.startTime ? 'error' : ''}`}
                    autoComplete="off"
                  />
                ) : (
                  <div className="time-slots-grid">
                    {loadingSlots || availabilityDate !== formData.date ? (
                      <div className="slots-loading">Cargando disponibilidad...</div>
                    ) : availableSlots.length === 0 ? (
                      <div className="no-slots-message">No hay horarios disponibles en el rango de atención.</div>
                    ) : (
                      availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`time-slot-btn ${slot.isConflict ? 'conflict' : 'available'} ${slot.conflictType === 'block' || slot.conflictType === 'both' ? 'blocked' : ''} ${formData.startTime === slot.time ? 'selected' : ''}`}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, startTime: slot.time }));
                            setErrors(prev => ({ ...prev, startTime: '' }));
                            setMessage((prev) => (prev.type === 'warning' ? { type: '', text: '' } : prev));
                          }}
                          title={slot.conflictTitle}
                        >
                          {slot.time}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
            {errors.startTime && <span className="error-text">{errors.startTime}</span>}
          </div>

          {/* Status */}
          <div className="form-group">
            <label htmlFor="status">
              Estado
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className={errors.status ? 'error' : ''}
            >
              <option value="scheduled">Programada</option>
              <option value="confirmed">Confirmada</option>
              <option value="cancelled">Cancelada</option>
              <option value="completed">Completada</option>
              <option value="no-show">No Asistió</option>
            </select>
            {errors.status && <span className="error-text">{errors.status}</span>}
          </div>
        </div>

        {/* Procedure */}
        <div className="form-group full-width">
          <label htmlFor="procedure">Procedimiento</label>
          <input
            id="procedure"
            name="procedure"
            type="text"
            value={formData.procedure}
            onChange={handleInputChange}
            maxLength={50}
            placeholder="Procedimiento de la cita"
            className={errors.procedure ? 'error' : ''}
          />
          {errors.procedure && <span className="error-text">{errors.procedure}</span>}
        </div>

        {/* Notes */}
        <div className="form-group full-width">
          <label htmlFor="notes">Notas</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={4}
            placeholder="Notas adicionales sobre la cita..."
          />
        </div>

        {/* Form Actions */}
        <div className="appointment-form-actions">
          {message.text && (
            <div className={`message ${message.type} appointment-form-actions-message`}>
              {message.text}
            </div>
          )}
          <div className="appointment-form-actions-buttons">
            {appointment && onDelete && (
              <Button
                type="button"
                variant="danger"
                onClick={onDelete}
                disabled={isLoading}
                style={{ marginRight: 'auto' }}
              >
                Eliminar
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading || isLoadingDoctors || !isDateValid}
            >
              {appointment ? 'Actualizar Cita' : 'Crear Cita'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AppointmentForm;
